import { OP_LEVELS, type OpLevel } from "../config/operatingPoints";
import type { NativePrediction, NativePredictions } from "../types/predictions";

export function getTier(prediction: NativePrediction): OpLevel | null {
  for (const level of OP_LEVELS) {
    if (prediction.operating_points[level]) return level;
  }
  return null;
}

// Cascade semantic: a finding passes `threshold` if its strictest true op-point
// is at least as strict. Device-engine may emit un-cascaded operating_points
// (only the recommended threshold flagged), so a raw lookup would miss findings
// flagged at a stricter level than the query.
//
// Special case `"recommended"` — for cortex device, each finding has its own
// `recommended_threshold` (per cortex.json finding_categories). The detected_at_recommended
// derived array is the source of truth at the cohort level. At the per-prediction
// level we don't have the recommended_threshold map here, so we treat any
// firing OP as detected (caller is expected to use detected_at_recommended for
// strict per-finding semantics).
export function isDetected(
  prediction: NativePrediction,
  threshold: OpLevel | "recommended" = "high_sensitivity",
): boolean {
  const tier = getTier(prediction);
  if (tier === null) return false;
  if (threshold === "recommended") return true;
  return OP_LEVELS.indexOf(tier) <= OP_LEVELS.indexOf(threshold);
}

export function getTopSlice(prediction: NativePrediction): string | null {
  return prediction.top_slices[0] ?? null;
}

// Cortex encodes a finding's attributes NOT as a field on the prediction, but
// as sibling predictions keyed `<condition>/<dim>/<value>` (e.g.
// "urinary/hydronephrosis/laterality/right"), each with its own probability.
// This mirrors web-demo-backend/src/cortex_structured.py (`_consolidate` +
// `build_default_phrase`): per dim keep the highest-probability value at or
// above the confidence threshold, resolve the severity/size mutual exclusion
// (radiologists don't say "mild moderate aneurysm"), and emit display-ready
// values in report order. Returns [] when the finding has no confidently
// predicted attribute. Used by the reviewer finding row so the reviewer sees
// the same descriptors the report phrases the finding with — not just the name.
const ATTR_CONFIDENCE_THRESHOLD = 0.5;

// Value display overrides per dim, mirroring cortex_structured.py's *_DISPLAY
// maps. Any value not listed falls back to underscores→spaces.
const ATTR_VALUE_DISPLAY: Record<string, Record<string, string>> = {
  calcification_status: {
    non_calcified: "non-calcified",
    partially_calcified: "partially calcified",
  },
  obstruction: {
    nonobstructing: "non-obstructing",
  },
};

function formatAttrValue(dim: string, value: string): string {
  return ATTR_VALUE_DISPLAY[dim]?.[value] ?? value.replace(/_/g, " ");
}

// The dims this cortex version (v1.4.8) actually emits — verified against the
// live cohort. Anything outside this set (e.g. a future `grade` / `*_location`
// dim) is appended rather than dropped, so the row never silently hides an
// attribute the model starts producing.
const KNOWN_ATTR_DIMS = new Set([
  "laterality",
  "acuity",
  "severity",
  "size",
  "obstruction",
  "calcification_status",
]);

export function getAttributeDescriptors(
  condition: string,
  predictions: NativePredictions,
): string[] {
  const selected = predictions[condition]?.attributes;
  if (Array.isArray(selected)) {
    // Drafter values have already passed the deployed clinical display gate.
    // Preserve the emitted list and tolerate new dimensions/values; this
    // consumer intentionally owns no taxonomy or probability threshold.
    return selected
      .filter(
        (attribute): attribute is { dim: string; value: string } =>
          typeof attribute?.dim === "string" && typeof attribute?.value === "string",
      )
      .map((attribute) => formatAttrValue(attribute.dim, attribute.value));
  }

  const prefix = `${condition}/`;
  // Highest-probability value per dim, among siblings at/above the threshold.
  const byDim: Record<string, { value: string; probability: number }> = {};
  for (const [key, pred] of Object.entries(predictions)) {
    if (!key.startsWith(prefix)) continue;
    const rest = key.slice(prefix.length).split("/");
    if (rest.length !== 2) continue; // direct <dim>/<value> children only
    const [dim, value] = rest;
    const probability = pred.probability ?? 0;
    if (probability < ATTR_CONFIDENCE_THRESHOLD) continue;
    const cur = byDim[dim];
    if (!cur || probability > cur.probability) byDim[dim] = { value, probability };
  }

  // Report order: laterality → acuity → severity-or-size → obstruction → calcification.
  const parts: string[] = [];
  if (byDim.laterality) parts.push(formatAttrValue("laterality", byDim.laterality.value));
  if (byDim.acuity) parts.push(formatAttrValue("acuity", byDim.acuity.value));
  const sev = byDim.severity;
  const size = byDim.size;
  if (sev && size) {
    parts.push(
      sev.probability >= size.probability
        ? formatAttrValue("severity", sev.value)
        : formatAttrValue("size", size.value),
    );
  } else if (sev) {
    parts.push(formatAttrValue("severity", sev.value));
  } else if (size) {
    parts.push(formatAttrValue("size", size.value));
  }
  if (byDim.obstruction) parts.push(formatAttrValue("obstruction", byDim.obstruction.value));
  if (byDim.calcification_status)
    parts.push(formatAttrValue("calcification_status", byDim.calcification_status.value));

  // Surface any dim the model adds in the future instead of dropping it.
  for (const dim of Object.keys(byDim).sort()) {
    if (!KNOWN_ATTR_DIMS.has(dim)) parts.push(formatAttrValue(dim, byDim[dim].value));
  }

  return parts;
}
